import { CODE_HEADERS, CodeChallenge, IDLE_LEFT_HEADER, REAUTH_REQUIRED_CODE, SESSION_IDLE_CODE, USER_ACTION_HEADER, type CodeResendResponse, type Problem } from "@genbaryoku/contract";
import { t } from "./labels.js";
import { idleHeaders } from "./lib/activity.js";
import { actionHeaders, isLoggedIn, noteIdleLeft, sentBeforeLogin, sessionLost } from "./lib/autoLogout.js";
import { reportMaintenance } from "./lib/maintenance.js";
import { recordApi } from "./lib/trail.js";
import { productHeaders } from "./lib/product.js";
import { currentView } from "./lib/view.js";

/**
 * API に毎回付けるヘッダー：製品（049）と、いまの版・5分 触っていないか（#123 の 2b。利用状況を PC版／スマホ版で分けて数え、
 * 開いたままの画面の読み直しを数えないためだけ）
 */
const baseHeaders = (): Record<string, string> => ({ ...productHeaders(), "x-gb-view": currentView(), ...idleHeaders() });

/** API のエラー（RFC 9457）。title は画面にそのまま出す日本語 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: Problem & Record<string, unknown>,
  ) {
    super(problem.title);
    this.name = "ApiError";
  }
  get code(): string | undefined {
    return this.problem.code;
  }
}

const OFFLINE: Problem = { type: "about:blank", status: 0, title: "つながりませんでした。電波の状態を確かめて、もう一度お試しください" };

/** API の文の {customer} などを会社の言葉に（title と欄ごとの errors の両方。027） */
function localize(p: Problem & Record<string, unknown>): Problem & Record<string, unknown> {
  const errors = p.errors && typeof p.errors === "object" ? Object.fromEntries(Object.entries(p.errors as Record<string, unknown>).map(([k, v]) => [k, typeof v === "string" ? t(v) : v])) : p.errors;
  return { ...p, title: t(p.title), ...(errors === undefined ? {} : { errors }) } as Problem & Record<string, unknown>;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * 確認コードの窓（#110、docs/42）。components/CodeDialog.tsx の CodeDialogHost が登録する。
 * API が 403 AUTH_CODE_REQUIRED（海外の IP からのログイン・会員登録・属性変更）を返したら窓を開き、
 * 入れたコードをヘッダーに付けて同じ操作を送り直す。結果（ログインなら Me）は元の呼び出しにそのまま返る＝画面ごとの作り直しは要らない
 */
export type CodePrompt = (
  challenge: CodeChallenge,
  actions: { submit: (code: string) => Promise<unknown>; resend: () => Promise<CodeResendResponse> },
) => Promise<unknown>;
let codePrompt: CodePrompt | null = null;
export function setCodePrompt(p: CodePrompt | null) {
  codePrompt = p;
}

/**
 * 本人確認の窓（#189、docs/76 ⑤）。components/ReauthDialog.tsx の ReauthDialogHost が登録する。
 * API が 403 REAUTH_REQUIRED（振込口座・ログイン方法・パスワードの設定・この会社を削除・退会）を返したら窓を開き、
 * 通ったら同じ操作を送り直す（確認コードの窓と同じ作り＝画面ごとの作り直しは要らない）。取消は ApiError（REAUTH_CANCELLED）で返す
 */
export type ReauthPrompt = () => Promise<void>;
let reauthPrompt: ReauthPrompt | null = null;
export function setReauthPrompt(p: ReauthPrompt | null) {
  reauthPrompt = p;
}

export async function api<T>(method: Method, path: string, body?: unknown, headers?: Record<string, string>, reauthed = false): Promise<T> {
  try {
    return await send<T>(method, path, body, headers);
  } catch (e) {
    // 本人確認の窓を通したあとに送り直すのは1回だけ（もう一度断られたら、その文のまま返す）
    if (!reauthed && e instanceof ApiError && e.code === REAUTH_REQUIRED_CODE && reauthPrompt) {
      await reauthPrompt();
      return api<T>(method, path, body, headers, true);
    }
    const ch = e instanceof ApiError && e.code === "AUTH_CODE_REQUIRED" ? CodeChallenge.safeParse(e.problem) : null;
    if (!ch?.success || !codePrompt) throw e;
    const challenge = ch.data;
    return (await codePrompt(challenge, {
      submit: (code) => send<T>(method, path, body, { ...headers, [CODE_HEADERS.challenge]: challenge.challengeToken, [CODE_HEADERS.code]: code }),
      resend: () => send<CodeResendResponse>("POST", "/auth/code/resend", { challengeToken: challenge.challengeToken }),
    })) as T;
  }
}

/**
 * 自動ログアウト（#189、docs/76）：答えを受け取ったら、無操作で切れるまでの残り（x-gb-idle-left）を覚える。
 * ログインしていた人の 401 SESSION_IDLE（無操作で切れた）＝止めの画面、401 AUTH_REQUIRED（ほかの理由で切れた）＝ログインの画面へ。
 * どちらも先に書きかけの入力を端末に退避する（lib/autoLogout.ts の sessionLost）。
 * ログインしていない画面（開いた直後・/handoff・/login・/invite など）の SESSION_IDLE は、ふつうの未ログインの 401 として扱う
 * （#192、docs/76 §0.3 テ：上限を過ぎた Cookie が残っていただけ。Cookie は API が消している。止めの画面を重ねると、下の「入る」などが押せない）
 */
function watchSession(res: Response, sentAction: boolean, sentAt: number, problem?: { code?: unknown }): void {
  if (sentBeforeLogin(sentAt)) return; // ログインより前に出た要求の答え（今のログインのことではない）
  const left = res.headers.get(IDLE_LEFT_HEADER);
  if (left !== null) noteIdleLeft(Number(left), sentAction && res.ok);
  if (res.status !== 401 || !problem || !isLoggedIn()) return;
  if (problem.code === SESSION_IDLE_CODE) sessionLost("idle");
  else if (problem.code === "AUTH_REQUIRED") sessionLost("expired");
}

async function send<T>(method: Method, path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  let res: Response;
  // 人の操作から 15 秒以内なら X-User-Action（passive の印があれば付けない）
  const hs = actionHeaders(headers);
  const sentAction = hs[USER_ACTION_HEADER] === "1";
  const sentAt = Date.now();
  try {
    // x-product は開発・テストで製品を指しているときだけ（049）。本番は Host が決める。headers は Idempotency-Key など（E1 の入金）
    const init: RequestInit = { method, credentials: "include", headers: { ...baseHeaders(), ...hs } };
    if (body !== undefined) {
      init.headers = { ...baseHeaders(), ...hs, "content-type": "application/json" };
      init.body = JSON.stringify(body);
    }
    res = await fetch(`/api/v1${path}`, init);
  } catch {
    recordApi(method, path, 0);
    throw new ApiError(0, OFFLINE);
  }
  recordApi(method, path, res.status);
  if (res.status === 204) {
    watchSession(res, sentAction, sentAt);
    return undefined as T;
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const p = (json && typeof json === "object" ? json : { title: "いま処理できませんでした" }) as Problem & Record<string, unknown>;
    reportMaintenance(p, res.status); // メンテナンス中（#186）なら画面全体に止めの画面
    watchSession(res, sentAction, sentAt, p);
    throw new ApiError(res.status, localize({ ...p, status: res.status }));
  }
  watchSession(res, sentAction, sentAt);
  return json as T;
}

/** 見せる文：ApiError ならその title（{customer} などは会社の言葉に）、それ以外は決まった文 */
export function messageOf(e: unknown): string {
  if (e instanceof ApiError) return t(e.problem.title);
  return "いま処理できませんでした。少し待ってからもう一度お試しください";
}

/**
 * 画像などファイルそのものを本文にして送る（社印・ロゴ、025。Content-Type はファイルの種類）。
 * signal で打ち切れる（受け取った書類は1件2分、S2）。打ち切ったときは OFFLINE にせず、打ち切りのエラーのまま投げる（呼ぶ側が文を決める）
 */
export async function apiUpload<T>(path: string, file: Blob, contentType: string, headers?: Record<string, string>, signal?: AbortSignal): Promise<T> {
  let res: Response;
  const hs = actionHeaders(headers);
  const sentAction = hs[USER_ACTION_HEADER] === "1";
  const sentAt = Date.now();
  try {
    res = await fetch(`/api/v1${path}`, { method: "POST", credentials: "include", headers: { ...baseHeaders(), ...hs, "content-type": contentType }, body: file, ...(signal ? { signal } : {}) });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiError(0, OFFLINE);
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const p = (json && typeof json === "object" ? json : { title: "いま処理できませんでした" }) as Problem & Record<string, unknown>;
    reportMaintenance(p, res.status); // メンテナンス中（#186）なら画面全体に止めの画面
    watchSession(res, sentAction, sentAt, p);
    throw new ApiError(res.status, localize({ ...p, status: res.status }));
  }
  watchSession(res, sentAction, sentAt);
  return json as T;
}

/**
 * PDF などファイルの中身を取る（docs/72 §10）。url は /api/v1 から始まる道。
 * 断られたら API の文で ApiError（503 PDF_BUSY なら「PDF の作成が混み合っている…」、401 ならログインの文）。
 * PDF は同時に作る数に上限があり、サーバーで順番を待つことがある＝返るまで数秒〜かかってよい
 */
export async function fetchFile(url: string, init?: { method?: "GET" | "POST"; body?: unknown; /** 答えのヘッダーを読む（送付状の見本の「本状含め」の枚数＝x-gb-total-pages、#223） */ onHeaders?: (h: Headers) => void }): Promise<Blob> {
  const method = init?.method ?? "GET";
  const path = url.replace(/^\/api\/v1/, "").split("?")[0] ?? url;
  let res: Response;
  const hs = actionHeaders(undefined);
  const sentAction = hs[USER_ACTION_HEADER] === "1";
  const sentAt = Date.now();
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      headers: { ...baseHeaders(), ...hs, ...(init?.body !== undefined ? { "content-type": "application/json" } : {}) },
      ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });
  } catch {
    recordApi(method, path, 0);
    throw new ApiError(0, OFFLINE);
  }
  recordApi(method, path, res.status);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let p: (Problem & Record<string, unknown>) | undefined;
    try {
      const json = text ? (JSON.parse(text) as unknown) : undefined;
      if (json && typeof json === "object") p = json as Problem & Record<string, unknown>;
    } catch {
      p = undefined;
    }
    reportMaintenance(p, res.status);
    watchSession(res, sentAction, sentAt, p);
    throw new ApiError(res.status, localize({ ...(p ?? { type: "about:blank", title: res.status === 401 ? "ログインしてください" : "PDF を作成できませんでした" }), status: res.status }));
  }
  watchSession(res, sentAction, sentAt);
  init?.onHeaders?.(res.headers);
  return res.blob();
}
